import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { User, ChevronDown, Search } from 'lucide-react';

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 1;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 0.625rem 2rem 0.625rem 3rem;
  background: ${props => props.theme === 'dark' ? '#3a3a3a' : 'white'};
  border: 1px solid ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#d1d5db'};
  border-radius: 6px;
  color: ${props => props.theme === 'dark' ? '#e0e0e0' : '#374151'};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    border-color: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#9ca3af'};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#3b82f6'};
    box-shadow: 0 0 0 3px ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${props => props.theme === 'dark' ? '#2a2a2a' : '#f3f4f6'};
  }
`;

const UserAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.color || '#3b82f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  flex-shrink: 0;
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
`;

const ChevronIconContainer = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${props => props.theme === 'dark' ? '#a0a0a0' : '#6b7280'};
  display: flex;
  align-items: center;
`;

const DropdownMenu = styled.div`
  position: fixed;
  top: ${props => props.top ? `${props.top}px` : '50px'};
  left: ${props => props.left ? `${props.left}px` : '50px'};
  width: ${props => props.width ? `${props.width}px` : '200px'};
  background: ${props => props.theme === 'dark' ? '#3a3a3a' : 'white'};
  border: 1px solid ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#d1d5db'};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  max-height: 250px;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme === 'dark' ? '#2a2a2a' : '#f1f1f1'};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme === 'dark' ? '#555' : '#c1c1c1'};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme === 'dark' ? '#777' : '#a8a8a8'};
  }
`;

const DropdownOption = styled.div`
  padding: 0.625rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  color: ${props => props.theme === 'dark' ? '#e0e0e0' : '#374151'};
  font-size: 0.875rem;
  
  &:hover {
    background: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6'};
  }
  
  &:first-of-type {
    border-radius: 6px 6px 0 0;
  }
  
  &:last-of-type {
    border-radius: 0 0 6px 6px;
  }
`;

const OptionAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.color || '#3b82f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
`;

const OptionText = styled.div`
  flex: 1;
`;

const OptionLabel = styled.div`
  font-weight: 500;
`;

const OptionRole = styled.div`
  font-size: 0.7rem;
  color: ${props => props.theme === 'dark' ? '#a0a0a0' : '#6b7280'};
  text-transform: capitalize;
`;

const SearchContainer = styled.div`
  padding: 0.5rem;
  border-bottom: 1px solid ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'};
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.5rem 0.5rem 2rem;
  background: ${props => props.theme === 'dark' ? '#2a2a2a' : '#f9fafb'};
  border: 1px solid ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#d1d5db'};
  border-radius: 4px;
  color: ${props => props.theme === 'dark' ? '#e0e0e0' : '#374151'};
  font-size: 0.8rem;
  outline: none;
  
  &:focus {
    border-color: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#3b82f6'};
    box-shadow: 0 0 0 2px ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }
  
  &::placeholder {
    color: ${props => props.theme === 'dark' ? '#a0a0a0' : '#9ca3af'};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme === 'dark' ? '#a0a0a0' : '#9ca3af'};
  pointer-events: none;
`;

const UserDropdown = ({ 
  users = [], 
  value, 
  onChange, 
  disabled = false, 
  theme = 'dark',
  placeholder = 'Select a user...',
  currentUser = null,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the wrapper and the portal dropdown
      const isOutsideWrapper = wrapperRef.current && !wrapperRef.current.contains(event.target);
      const isOutsidePortal = !event.target.closest('[data-portal-dropdown]');
      
      if (isOutsideWrapper && isOutsidePortal) {
        setIsOpen(false);
        setSearchTerm(''); // Clear search when closing
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return user.full_name.toLowerCase().includes(searchLower) ||
           user.username?.toLowerCase().includes(searchLower) ||
           user.email?.toLowerCase().includes(searchLower);
  });

  const selectedUser = users.find(u => u.id === parseInt(value));

  // Debug logging
  console.log('UserDropdown - selectedUser:', selectedUser?.full_name, 'currentUser:', currentUser?.full_name, 'match:', selectedUser?.full_name === currentUser?.full_name);
  console.log('UserDropdown - users:', users.map(u => ({ id: u.id, name: u.full_name, role: u.role })));

  const getInitials = (fullName) => {
    const parts = fullName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  const calculateMenuPosition = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const position = {
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      };
      console.log('UserDropdown position calculation:', position, 'rect:', rect);
      return position;
    }
    console.log('UserDropdown: wrapperRef.current is null');
    return { top: 0, left: 0, width: 0 };
  };

  const handleSelect = (userId) => {
    console.log('UserDropdown handleSelect called with userId:', userId);
    onChange({ target: { value: String(userId) } });
    setIsOpen(false);
    setSearchTerm(''); // Clear search when selecting
  };

  const handleToggle = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      console.log('UserDropdown toggle:', newIsOpen);
      setIsOpen(newIsOpen);
      if (newIsOpen) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          const position = calculateMenuPosition();
          console.log('Setting menu position:', position);
          setMenuPosition(position);
        }, 10);
      }
    }
  };

  return (
    <DropdownWrapper ref={wrapperRef} {...props}>
      <UserAvatar color={selectedUser?.avatar_color || '#4a4a4a'}>
        {selectedUser ? getInitials(selectedUser.full_name) : <User size={14} />}
      </UserAvatar>
      
      <DropdownButton
        theme={theme}
        onClick={handleToggle}
        disabled={disabled}
        type="button"
        data-cy="user-dropdown-button"
      >
        {selectedUser ? (
          selectedUser.full_name === currentUser?.full_name 
            ? `${selectedUser.full_name} (Me)` 
            : selectedUser.full_name
        ) : placeholder}
      </DropdownButton>
      
      <ChevronIconContainer theme={theme}>
        <ChevronDown size={16} />
      </ChevronIconContainer>

      {isOpen && !disabled && (() => {
        console.log('Rendering portal with position:', menuPosition);
        return createPortal(
          <DropdownMenu 
            theme={theme}
            top={menuPosition.top}
            left={menuPosition.left}
            width={menuPosition.width}
            data-portal-dropdown="true"
          >
          <SearchContainer theme={theme}>
            <SearchIcon theme={theme}>
              <Search size={14} />
            </SearchIcon>
            <SearchInput
              ref={searchInputRef}
              theme={theme}
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </SearchContainer>
          {filteredUsers.length > 0 ? (
            filteredUsers.map(user => (
              <DropdownOption
                key={user.id}
                theme={theme}
                onClick={() => handleSelect(user.id)}
              >
                <OptionAvatar color={user.avatar_color}>
                  {getInitials(user.full_name)}
                </OptionAvatar>
                <OptionText>
                  <OptionLabel>
                    {user.full_name === currentUser?.full_name 
                      ? `${user.full_name} (Me)` 
                      : user.full_name}
                  </OptionLabel>
                  {user.role && <OptionRole theme={theme}>{user.role.replace('_', ' ')}</OptionRole>}
                </OptionText>
              </DropdownOption>
            ))
          ) : (
            <DropdownOption theme={theme} style={{ cursor: 'default', opacity: 0.7 }}>
              <OptionText>
                <OptionLabel>No users found</OptionLabel>
              </OptionText>
            </DropdownOption>
          )}
          </DropdownMenu>,
          document.body
        );
      })()}
    </DropdownWrapper>
  );
};

export default UserDropdown;
